import {
  isObject,
  isNumber,
  isString,
  isArray,
  isBoolean,
} from 'utils/typeUtils';

/**
 * 顧客情報 from api
 */
export type OriginalCustomer = {
  id: number;
  name: string;
  detail: string;
  status: boolean;
  poseidon_url: string;
  system_update_group: string;
};

/**
 * 新規登録された顧客情報 from api
 * NOTE: 顧客情報登録 API のレスポンス (CustomerWithToken)
 */
export type OriginalNewCustomer = {
  id: number;
  name: string;
  detail: string;
  token: string;
};

/**
 * 顧客情報 from api のユーザ定義型ガード
 */
export const isOriginalCustomer = (arg: unknown): arg is OriginalCustomer => {
  const a = arg as OriginalCustomer;

  return (
    isObject(a) &&
    isNumber(a.id) &&
    isString(a.name) &&
    isString(a.detail) &&
    isBoolean(a.status) &&
    isString(a.poseidon_url) &&
    isString(a.system_update_group)
  );
};

/**
 * 顧客情報 from api の配列のユーザ定義型ガード
 */
export const isOriginalCustomers = (arg: unknown): arg is OriginalCustomer[] =>
  isArray(arg, isOriginalCustomer);

/**
 * 新規登録された顧客情報 from api のユーザ定義型ガード
 */
export const isOriginalNewCustomer = (
  arg: unknown,
): arg is OriginalNewCustomer => {
  const a = arg as OriginalNewCustomer;

  return (
    isObject(a) &&
    isNumber(a.id) &&
    isString(a.name) &&
    isString(a.detail) &&
    isString(a.token)
  );
};

/**
 * お知らせ登録画面用の顧客情報
 */
export type CustomerForNotification = OriginalCustomer & {
  isSelected: boolean;
};

export const createCustomerForNotifications = (
  customers: OriginalCustomer[],
): CustomerForNotification[] =>
  customers.map((customer) => ({ ...customer, isSelected: false }));

export const createCustomerForNotificationsWithTargets = (
  customers: OriginalCustomer[],
  targets: number[],
): CustomerForNotification[] =>
  customers.map((customer) => ({
    ...customer,
    isSelected: targets.includes(customer.id),
  }));

/**
 * 顧客一覧画面の顧客情報
 */
export type Customer = Pick<
  OriginalCustomer,
  'id' | 'name' | 'detail' | 'status' | 'poseidon_url' | 'system_update_group'
>;

export const createCustomers = (customers: OriginalCustomer[]): Customer[] =>
  customers.map((customer) => ({ ...customer }));

/**
 * 契約種別
 */
export const CHANNELS = {
  STABLE: 'stable',
  BETA: 'beta',
} as const;

/**
 * 契約種別
 */
export type Channel = typeof CHANNELS[keyof typeof CHANNELS];

/**
 * 契約種別の表示用文字列
 */
export const CHANNEL_LABELS = {
  [CHANNELS.STABLE]: 'stable',
  [CHANNELS.BETA]: 'beta',
} as const;

/**
 * 環境種別
 */
export const ENVIRONMENT_TYPES = {
  GCP: 'gcp',
  AWS: 'aws',
} as const;

/**
 * 契約種別
 */
export type EnvironmentType =
  typeof ENVIRONMENT_TYPES[keyof typeof ENVIRONMENT_TYPES];

/**
 * 新規作成用の顧客情報
 */
export type NewCustomer = {
  name: string;
  detail: string;
  environment: string;
  environmentType: EnvironmentType;
  corporateNumber: string;
  channel: Channel;
  poseidonUrl: string;
  systemUpdateGroup: string;
  iapClientId: string;
  iapClientSecret: string;
  poseidonAgentCpu?: string;
  poseidonAgentMemory?: string;
  enableVerticalPodAutoScaler?: boolean;
};

/**
 * 更新用の顧客情報
 */
export type UpdateCustomer = {
  name: string;
  detail: string;
  environment: string;
  environmentType: EnvironmentType;
  corporateNumber: string;
  channel: Channel;
  poseidonUrl: string;
  systemUpdateGroup: string;
  iapClientId: string;
  iapClientSecret: string;
  status: boolean;
  poseidonAgentCpu?: string;
  poseidonAgentMemory?: string;
  enableVerticalPodAutoScaler?: boolean;
};

/**
 * 顧客情報詳細 from api
 */
export type OriginalCustomerDetail = {
  name: string;
  detail: string;
  environment: string;
  environmentType: EnvironmentType;
  corporateNumber: string;
  channel: Channel;
  poseidonUrl: string;
  systemUpdateGroup: string;
  iapClientId: string;
  iapClientSecret: string;
  currentSystemVersion: string;
  targetSystemVersion: string;
  status: boolean;
  poseidonAgentCpu?: string;
  poseidonAgentMemory?: string;
  enableVerticalPodAutoScaler?: boolean;
};

/**
 * 環境種別の型ガード
 */
const isEnvironmentType = (arg: unknown): arg is EnvironmentType => {
  const v = arg as EnvironmentType;
  const environmentTypes = Object.values(ENVIRONMENT_TYPES);

  return environmentTypes.includes(v);
};

/**
 * 契約種別の型ガード
 */
const isChannel = (arg: unknown): arg is Channel => {
  const v = arg as Channel;
  const channels = Object.values(CHANNELS);

  return channels.includes(v);
};

/**
 * 新規登録された顧客情報詳細 from api のユーザ定義型ガード
 */
export const isOriginalCustomerDetail = (
  arg: unknown,
): arg is OriginalCustomerDetail => {
  const a = arg as OriginalCustomerDetail;

  return (
    isObject(a) &&
    isString(a.name) &&
    isString(a.detail) &&
    isString(a.environment) &&
    isEnvironmentType(a.environmentType) &&
    isString(a.corporateNumber) &&
    isChannel(a.channel) &&
    isString(a.poseidonUrl) &&
    isString(a.systemUpdateGroup) &&
    isString(a.iapClientId) &&
    isString(a.iapClientSecret) &&
    isString(a.currentSystemVersion) &&
    isString(a.targetSystemVersion) &&
    isBoolean(a.status) &&
    (a.poseidonAgentCpu === undefined || isString(a.poseidonAgentCpu)) &&
    (a.poseidonAgentMemory === undefined || isString(a.poseidonAgentMemory)) &&
    (a.enableVerticalPodAutoScaler === undefined ||
      isBoolean(a.enableVerticalPodAutoScaler))
  );
};

/**
 * Form用の顧客情報
 */
export type CustomerForForms = OriginalCustomer & {
  isSelected: boolean;
};

export const createCustomerForForms = (
  customers: OriginalCustomer[],
): CustomerForForms[] =>
  customers.map((customer) => ({ ...customer, isSelected: false }));

export const createCustomerForFormsWithTargets = (
  customers: OriginalCustomer[],
  targets: number[],
): CustomerForForms[] =>
  customers.map((customer) => ({
    ...customer,
    isSelected: targets.includes(customer.id),
  }));
